import { Check, ChevronsUpDown, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import { useGetModalCourses } from '@/hooks/queries/useModalCourses';
import type { Course } from '@/lib/types';
import { cn, uniqueByCourseId } from '@/lib/utils';

type CourseItem = Pick<Course, 'course_id' | 'course_name'>;

interface CourseMultiSelectProps {
	selectedCourses: CourseItem[];
	onChange: (courses: CourseItem[]) => void;
}

const CourseMultiSelect = ({ selectedCourses, onChange }: CourseMultiSelectProps) => {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState('');
	const containerRef = useRef<HTMLDivElement>(null);
	const inputRef = useRef<HTMLInputElement>(null);
	const { data: allCourses, isFetched } = useGetModalCourses();

	useEffect(() => {
		if (!open) return;

		const handlePointerDown = (e: PointerEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};

		document.addEventListener('pointerdown', handlePointerDown);
		return () => document.removeEventListener('pointerdown', handlePointerDown);
	}, [open]);

	const uniqueSelected = useMemo(() => uniqueByCourseId(selectedCourses), [selectedCourses]);

	const filteredCourses = useMemo(() => {
		if (!allCourses) return [];
		if (!search.trim()) return allCourses;
		return allCourses.filter((c) => c.course_name.toLowerCase().includes(search.toLowerCase()));
	}, [allCourses, search]);

	const selectedIds = useMemo(
		() => new Set(uniqueSelected.map((c) => c.course_id)),
		[uniqueSelected]
	);

	const toggleCourse = (course: CourseItem) => {
		if (selectedIds.has(course.course_id)) {
			onChange(uniqueSelected.filter((c) => c.course_id !== course.course_id));
		} else {
			onChange([...uniqueSelected, course]);
		}
	};

	const removeCourse = (courseId: string) => {
		onChange(uniqueSelected.filter((c) => c.course_id !== courseId));
	};

	const handleToggle = () => {
		setOpen((prev) => {
			if (!prev) {
				setTimeout(() => inputRef.current?.focus(), 0);
			}
			return !prev;
		});
	};

	return (
		<div className="space-y-2">
			<div ref={containerRef} className="relative">
				<Button
					type="button"
					variant="outline"
					role="combobox"
					aria-expanded={open}
					onClick={handleToggle}
					className="w-full justify-between focus-visible:border-purple-200   focus-visible:ring-0 focus-visible:shadow-none focus-visible:ring-offset-0 font-normal"
				>
					<span className="truncate text-primary">
						{uniqueSelected.length > 0
							? `${uniqueSelected.length} course${uniqueSelected.length !== 1 ? 's' : ''} selected`
							: 'Select courses...'}
					</span>
					<ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
				</Button>

				{open && (
					<div
						className="fixed mt-1 rounded-md border bg-popover text-popover-foreground shadow-md z-50"
						style={{ width: containerRef.current?.offsetWidth }}
					>
						<div className="p-2 border-b">
							<input
								ref={inputRef}
								type="text"
								placeholder="Search courses..."
								value={search}
								onChange={(e) => setSearch(e.target.value)}
								className="w-full px-2 py-1.5 text-sm bg-transparent outline-none placeholder:text-muted-foreground"
							/>
						</div>
						<div className="max-h-[200px] overflow-y-auto overscroll-contain p-1">
							{!isFetched && (
								<p className="py-4 text-center text-sm text-muted-foreground">Loading...</p>
							)}
							{isFetched && filteredCourses.length === 0 && (
								<p className="py-4 text-center text-sm text-muted-foreground">No courses found.</p>
							)}
							{filteredCourses.map((course) => {
								const isSelected = selectedIds.has(course.course_id);
								return (
									<div
										key={course.course_id}
										role="option"
										aria-selected={isSelected}
										tabIndex={0}
										onClick={() => toggleCourse(course)}
										onKeyDown={(e) => {
											if (e.key === 'Enter' || e.key === ' ') {
												e.preventDefault();
												toggleCourse(course);
											}
										}}
										className={cn(
											'flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-sm cursor-pointer hover:bg-accent',
											isSelected && 'bg-accent/50'
										)}
									>
										<span
											className={cn(
												'grid place-content-center h-4 w-4 shrink-0 rounded-md border border-primary',
												isSelected && 'bg-primary text-primary-foreground'
											)}
										>
											{isSelected && <Check className="h-4 w-4" />}
										</span>
										<span className="truncate">{course.course_name}</span>
									</div>
								);
							})}
						</div>
					</div>
				)}
			</div>

			{uniqueSelected.length > 0 && (
				<div className="flex flex-wrap gap-1.5">
					{uniqueSelected.map((course) => (
						<span
							key={course.course_id}
							className="inline-flex items-center gap-1 rounded-md bg-primary px-2.5 py-1 text-sm font-semibold text-primary-foreground"
						>
							{course.course_name}
							<button
								type="button"
								onClick={() => removeCourse(course.course_id)}
								className="ml-0.5 rounded-full hover:bg-primary-foreground/20"
							>
								<X className="h-3 w-3" />
								<span className="sr-only">Remove {course.course_name}</span>
							</button>
						</span>
					))}
				</div>
			)}
		</div>
	);
};

export default CourseMultiSelect;
